import React, { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Comment } from '@ant-design/compatible';
import formatDate from 'vl-common/src/lib/formatDate';
import { UserAvatar } from 'vl-common/src/components/UserAvatar';
import { NoteEditor } from 'vl-common/src/components/NoteEditor';
import { Note } from 'vl-common/src/api/api.notes';
import styled from '@emotion/styled';

export type NoteWithReplies = Note & {
  replies: NoteWithReplies[];
  isNoteConversationStarter: boolean;
  isParentNoteClinVisible: boolean;
};
interface PatientNoteProps {
  patient_guid: string; // the patient to which these notes relate
  currentNoteId: number; // the currently "active" noteId
  setCurrentNoteId: Dispatch<SetStateAction<number>>; // update the currentNoteId (0 => nothing active)
  note: NoteWithReplies; // the current note properties
  case_id?: number; // the case to which these notes apply
}

const ActionButton = styled.button`
  font-size: 12px;
  background: none;
  border: none;
  margin: 0;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.3s;
  &:hover {
    color: rgba(0, 0, 0, 0.85);
  }
`;

// A recursive sub-tree of comments along with associated NoteEditor(s) and child notes.
export const PatientNote: React.FC<PatientNoteProps> = ({
  patient_guid,
  note,
  currentNoteId,
  setCurrentNoteId,
  case_id
}) => {
  const { t } = useTranslation('common');
  const [mode, setMode] = useState<'reply' | 'edit' | 'add'>('add');
  const showEditNoteEditor = note.note_id === currentNoteId && mode === 'edit';
  const showReplyNoteEditor = note.note_id === currentNoteId && mode === 'reply';
  const commentator = note.user_guid;

  if (!note) return null;

  return (
    <Comment
      actions={[
        <ActionButton
          key="comment-nested-reply-to"
          onClick={() => {
            setMode('reply');
            setCurrentNoteId((prev) => note.note_id || prev);
          }}
          data-testid="reply-note-button"
        >
          {t('Reply')}
        </ActionButton>,
        <ActionButton
          key="comment-nested-edit"
          onClick={() => {
            setMode('edit');
            setCurrentNoteId((prev) => note.note_id || prev);
          }}
          data-testid="edit-note-button"
        >
          {t('Edit')}
        </ActionButton>
      ]}
      author={
        <span>
          {note.added_by} {t('at')}{' '}
          {formatDate(note.date_added, 'Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57')}
          {note.clin_visible && <span>&nbsp;(shared with clinicians)</span>}
        </span>
      }
      avatar={<UserAvatar user_guid={commentator} />}
      content={<p>{note.note_text}</p>}
    >
      <>
        {showEditNoteEditor && (
          <NoteEditor
            onClosed={() => setCurrentNoteId(0)}
            mode="edit"
            patient_guid={patient_guid}
            note={note}
            case_id={case_id}
          />
        )}
        {note.replies.map((n: NoteWithReplies) => (
          <PatientNote
            key={n.note_id}
            note={n}
            patient_guid={patient_guid}
            currentNoteId={currentNoteId}
            setCurrentNoteId={setCurrentNoteId}
            case_id={case_id}
          />
        ))}
        {showReplyNoteEditor && (
          <NoteEditor
            onClosed={() => setCurrentNoteId(0)}
            mode="reply"
            patient_guid={patient_guid}
            note={note}
            case_id={case_id}
          />
        )}
      </>
    </Comment>
  );
};

export default PatientNote;
